/**
 * Share controls on the donor web (GH-864e, plan §1.4 "Share prompts"): the icon in the org and
 * event headers, and "Share with friends" on a received receipt.
 *
 * ## Which link
 *
 * A signed-in donor's link comes from `GET /v1/donor/share-link`: a member's carries `?ref=` (their
 * share code, which can earn influence credit), anyone else's is plain. A stranger shares the page's
 * own address. Sharing itself is never gated — only the credit for it (plan §1.1).
 *
 * The link is fetched **when the control appears**, not when it is tapped. Browsers only open a
 * share sheet inside a fresh tap, and a sheet opened after a network round trip is one Safari
 * refuses; with the link already in hand the tap opens the sheet synchronously.
 *
 * ## Where these never appear
 *
 * Not on the give box, the checkout, the payment sheet or the embed (DEC-051): the receipt's control
 * is drawn only once the gift is received, after the success state and never before or during
 * payment. And nothing here is drawn while `FEATURE_MEMBERSHIP` is off.
 */
import { useEffect, useState } from "react";
import { MEMBERSHIP_COPY, type ShareLinkResponse } from "@givehere/contracts";
import { fetchShareLink, recordShare, storedToken } from "./donor-session.js";
import { ShareIcon } from "./icons.js";
import { useMembershipPulse } from "./membership-hooks.js";
import { plainShareUrl, shareOrCopy, type ShareOutcome, type ShareTarget } from "./share.js";

type ShareKind = "organization" | "event" | "receipt";

type ResolvedLink = {
  readonly url: string;
  readonly text: string | null;
  readonly credited: boolean;
};

const plain = (target: ShareTarget): ResolvedLink => ({
  url: plainShareUrl(target),
  text: null,
  credited: false,
});

const fromServer = (link: ShareLinkResponse): ResolvedLink => ({
  url: link.url,
  text: link.messages[0]?.text ?? null,
  credited: link.credited,
});

function useShare(target: ShareTarget, kind: ShareKind, enabled: boolean) {
  const [link, setLink] = useState<ResolvedLink>(() => plain(target));
  const [outcome, setOutcome] = useState<ShareOutcome | null>(null);
  const targetKey = "eventId" in target ? `e:${target.eventId}` : `o:${target.organizationId}`;

  useEffect(() => {
    if (!enabled) return;
    const token = storedToken();
    if (!token) return;
    let live = true;
    fetchShareLink(token, target).then(
      (value) => live && value !== null && setLink(fromServer(value)),
      // Any failure leaves the plain link, which always works and simply carries no credit.
      () => undefined,
    );
    return () => {
      live = false;
    };
    // `targetKey` names the target; the object itself is rebuilt by every host render.
  }, [enabled, targetKey]);

  const share = async () => {
    setOutcome(null);
    const result = await shareOrCopy({ url: link.url, ...(link.text ? { text: link.text } : {}) });
    setOutcome(result);
    const token = storedToken();
    if (link.credited && token && (result === "shared" || result === "copied")) {
      // The streak is cosmetic; a refused count changes nothing the donor can see.
      void recordShare(token, kind).catch(() => undefined);
    }
  };
  return { share, outcome };
}

function OutcomeLine({ outcome, className }: { outcome: ShareOutcome | null; className: string }) {
  // Always mounted, so a screen reader hears the change rather than a node appearing.
  return (
    <span className={className} role="status">
      {outcome === "copied"
        ? MEMBERSHIP_COPY.linkCopied
        : outcome === "failed"
          ? "Couldn’t share the link"
          : ""}
    </span>
  );
}

/** The header icon on an organization's page or an event's. Hidden while the gate is off. */
export function ShareIconButton({
  target,
  name,
}: {
  target: ShareTarget;
  /** The organization's or event's own name, for the control's accessible name. */
  name: string;
}) {
  const pulse = useMembershipPulse();
  const open = pulse !== undefined && pulse !== null;
  const { share, outcome } = useShare(target, "eventId" in target ? "event" : "organization", open);
  if (!open) return null;
  return (
    <span className="share-icon">
      <button
        type="button"
        className="fineprint__icon"
        aria-label={`Share ${name}`}
        onClick={() => void share()}
      >
        <ShareIcon />
      </button>
      <OutcomeLine outcome={outcome} className="share-icon__status" />
    </span>
  );
}

/**
 * "Share with friends" under a received receipt. The host draws it only in the received state; it
 * hides itself while the gate is off. The link is the organization's — the gift is the donor's
 * own business, so nothing about it (amount, receipt, date) is in the link or the message.
 */
export function ReceiptShare({ organizationId }: { organizationId: string }) {
  const pulse = useMembershipPulse();
  const open = pulse !== undefined && pulse !== null;
  const { share, outcome } = useShare({ organizationId }, "receipt", open);
  if (!open) return null;
  return (
    <section className="receipt-share" aria-label={MEMBERSHIP_COPY.shareWithFriends}>
      <button type="button" className="pay pay--outline" onClick={() => void share()}>
        {MEMBERSHIP_COPY.shareWithFriends}
      </button>
      <OutcomeLine outcome={outcome} className="receipt-share__status muted small" />
    </section>
  );
}
